import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, Users, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (search = "") => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/customers?search=${search}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data pelanggan (unauthorized).");
      } else {
        toast.error("Gagal memuat data pelanggan");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(searchQuery);
  };

  const getStatusBadge = (status) => {
    const styles = {
      unpaid: "status-unpaid",
      pending: "status-pending",
      paid: "status-paid",
    };
    const labels = {
      unpaid: "Belum Bayar",
      pending: "Pending",
      paid: "Lunas",
    };
    return <span className={styles[status] || styles.unpaid}>{labels[status] || status}</span>;
  };

  return (
    <AdminLayout title="Daftar Pelanggan">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Cari berdasarkan ID Pelanggan atau Nama..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 pl-11"
                  data-testid="customer-search-input"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              <Button type="submit" className="h-11 px-6">
                Cari
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : customers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                Tidak Ada Pelanggan
              </h3>
              <p className="text-slate-600">
                {searchQuery ? "Tidak ditemukan pelanggan dengan kriteria tersebut" : "Upload PDF untuk menambah data tagihan"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Users className="w-5 h-5" />
                {customers.length} Pelanggan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="table-wrapper">
                <table className="data-table" data-testid="customers-table">
                  <thead>
                    <tr>
                      <th>No. Sambungan</th>
                      <th>Nama</th>
                      <th>Alamat</th>
                      <th>Tagihan Terakhir</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.no_samb}>
                        <td className="font-mono font-medium">{customer.no_samb}</td>
                        <td>{customer.nama}</td>
                        <td className="text-slate-500">{customer.alamat || "-"}</td>
                        <td>{customer.latest_bulan}</td>
                        <td>{getStatusBadge(customer.latest_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </AdminLayout>
  );
}
